import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import { AppShell } from '@/app/layout/AppShell'
import { AdminLayout } from '@/features/admin/AdminLayout'
import { AuditPage } from '@/features/admin/AuditPage'
import { OverviewPage } from '@/features/admin/OverviewPage'
import { GroupsPage } from '@/features/admin/GroupsPage'
import { SourcesPage } from '@/features/admin/SourcesPage'
import { UsersPage } from '@/features/admin/UsersPage'
import { TemplateEditorPage } from '@/features/documents/editor/TemplateEditorPage'
import { PrintRunPage } from '@/features/documents/print/PrintRunPage'
import { RunListPage } from '@/features/documents/runs/RunListPage'
import { RunPage } from '@/features/documents/runs/RunPage'
import { DocumentListPage } from '@/features/documents/list/DocumentListPage'
import { ChangePasswordPage } from '@/features/auth/ChangePasswordPage'
import { LoginPage } from '@/features/auth/LoginPage'
import { McpConsentPage } from '@/features/auth/McpConsentPage'
import { RequireAuth } from '@/features/auth/RequireAuth'
import { RequirePermission } from '@/features/auth/RequirePermission'
import { ReportIndexRedirect, ReportPage } from '@/features/report/ReportPage'
import { SystemPage } from '@/features/system/SystemPage'
import {
  ADMIN_PERMISSIONS,
  HR_PERMISSIONS,
  HR_REQUEST_PERMISSIONS,
  HR_TASK_PERMISSIONS,
} from '@/app/layout/nav-items'
import { ApprovalsPage } from '@/features/hr/ApprovalsPage'
import { AttendancePage } from '@/features/hr/AttendancePage'
import { InterventionsPage } from '@/features/hr/InterventionsPage'
import { LeavesPage } from '@/features/hr/LeavesPage'
import { PersonnelPage } from '@/features/hr/PersonnelPage'
import { ProcessesPage } from '@/features/hr/ProcessesPage'
import { TasksPage } from '@/features/hr/TasksPage'
import { ChequesPage } from '@/features/cash/ChequesPage'
import { HealthPage } from '@/features/cash/HealthPage'
import { HomePage as CashHomePage } from '@/features/cash/HomePage'
import { ManualItemsPage } from '@/features/cash/ManualItemsPage'
import { ParallelPage } from '@/features/cash/ParallelPage'
import { PaymentListPage } from '@/features/cash/PaymentListPage'
import { PlanningPage } from '@/features/cash/PlanningPage'
import { SettingsPage as CashSettingsPage } from '@/features/cash/SettingsPage'
import { VariancePage } from '@/features/cash/VariancePage'
import { WeeklyCashPage } from '@/features/cash/WeeklyCashPage'

/**
 * Sabit ekranlar (sistem, yönetim) + meta'dan gelen raporlar. Sabit ekranlarda yetki
 * kontrolü yaprak rotada `RequirePermission`; raporlarda Cube meta'sının kendisi.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/giris" element={<LoginPage />} />

      <Route element={<RequireAuth />}>
        <Route path="/hesap/parola" element={<ChangePasswordPage />} />
        {/* Claude bağlantısı onayı: Claude'un OAuth akışından gelinir, uygulama kabuğu yok. */}
        <Route path="/oauth/mcp" element={<McpConsentPage />} />

        {/* Baskı sayfası AppShell dışındadır (menü/başlık basılmaz); açık tema sayfada zorlanır. */}
        <Route
          path="/yazdir/belge/:id/:runId"
          element={
            <RequirePermission permission="report.documents:view">
              <PrintRunPage />
            </RequirePermission>
          }
        />

        <Route element={<AppShell />}>
          <Route index element={<ReportIndexRedirect />} />

          {/* Belgeler: ekran `report.documents:view`; rapor bazında görünürlük/`can_edit` backend'de. */}
          <Route
            path="/belgeler"
            element={
              <RequirePermission permission="report.documents:view">
                <Outlet />
              </RequirePermission>
            }
          >
            <Route index element={<DocumentListPage />} />
            <Route path=":id/duzenle" element={<TemplateEditorPage />} />
            <Route path=":id" element={<RunListPage />} />
            <Route path=":id/:runId" element={<RunPage />} />
          </Route>

          {/* İnsan Kaynakları (docs/IK-PANEL-PLAN.md); firma kapsamı backend ve Cube'da. */}
          <Route path="/ik">
            <Route index element={<Navigate to="personel" replace />} />
            <Route
              path="personel"
              element={
                <RequirePermission permission="hr.personnel:view">
                  <PersonnelPage />
                </RequirePermission>
              }
            />
            <Route
              path="giris-cikis"
              element={
                <RequirePermission permission="hr.attendance:view">
                  <AttendancePage />
                </RequirePermission>
              }
            />
            <Route
              path="izinler"
              element={
                <RequirePermission permission="hr.leave:view">
                  <LeavesPage />
                </RequirePermission>
              }
            />
            <Route
              path="onaylar"
              element={
                <RequirePermission permission={HR_REQUEST_PERMISSIONS}>
                  <ApprovalsPage />
                </RequirePermission>
              }
            />
            <Route
              path="surecler"
              element={
                <RequirePermission permission={HR_PERMISSIONS}>
                  <ProcessesPage />
                </RequirePermission>
              }
            />
            <Route
              path="mudahaleler"
              element={
                <RequirePermission permission={HR_PERMISSIONS}>
                  <InterventionsPage />
                </RequirePermission>
              }
            />
            <Route
              path="islerim"
              element={
                <RequirePermission permission={HR_TASK_PERMISSIONS}>
                  <TasksPage />
                </RequirePermission>
              }
            />
          </Route>

          {/* Nakit planı (docs/NAKIT-PLANI-PLAN.md); firma kapsamı Cube'da. */}
          <Route path="/nakit">
            <Route
              index
              element={
                <RequirePermission permission="cash.view">
                  <CashHomePage />
                </RequirePermission>
              }
            />
            <Route
              path="odeme-listesi"
              element={
                <RequirePermission permission="cash.view">
                  <PaymentListPage />
                </RequirePermission>
              }
            />
            <Route
              path="plan-gerceklesen"
              element={
                <RequirePermission permission="cash.view">
                  <VariancePage />
                </RequirePermission>
              }
            />
            <Route
              path="paralel"
              element={
                <RequirePermission permission="cash.view">
                  <ParallelPage />
                </RequirePermission>
              }
            />
            <Route
              path="haftalik"
              element={
                <RequirePermission permission="cash.view">
                  <WeeklyCashPage />
                </RequirePermission>
              }
            />
            <Route
              path="odeme-planlama"
              element={
                <RequirePermission permission="cash.view">
                  <PlanningPage />
                </RequirePermission>
              }
            />
            <Route
              path="erp-disi"
              element={
                <RequirePermission permission="cash.view">
                  <ManualItemsPage />
                </RequirePermission>
              }
            />
            <Route
              path="cekler"
              element={
                <RequirePermission permission="cash.view">
                  <ChequesPage />
                </RequirePermission>
              }
            />
            <Route
              path="veri-sagligi"
              element={
                <RequirePermission permission="cash.view">
                  <HealthPage />
                </RequirePermission>
              }
            />
            <Route
              path="ayarlar"
              element={
                <RequirePermission permission="cash.view">
                  <CashSettingsPage />
                </RequirePermission>
              }
            />
          </Route>

          <Route
            path="/sistem"
            element={
              <RequirePermission permission="system.status:view">
                <SystemPage />
              </RequirePermission>
            }
          />

          <Route path="/yonetim" element={<AdminLayout />}>
            <Route index element={<Navigate to="genel-bakis" replace />} />
            <Route
              path="genel-bakis"
              element={
                <RequirePermission permission={ADMIN_PERMISSIONS}>
                  <OverviewPage />
                </RequirePermission>
              }
            />
            <Route
              path="kullanicilar"
              element={
                <RequirePermission permission="admin.users:manage">
                  <UsersPage />
                </RequirePermission>
              }
            />
            <Route
              path="gruplar"
              element={
                <RequirePermission permission="admin.groups:manage">
                  <GroupsPage />
                </RequirePermission>
              }
            />
            <Route
              path="denetim"
              element={
                <RequirePermission permission="admin.audit:view">
                  <AuditPage />
                </RequirePermission>
              }
            />
            <Route
              path="kaynaklar"
              element={
                <RequirePermission permission="admin.sources:view">
                  <SourcesPage />
                </RequirePermission>
              }
            />
          </Route>

          {/* Rapor route'ları Cube meta'sından gelir; eşleşmeyen adres orada "bulunamadı" olur. */}
          <Route path="*" element={<ReportPage />} />
        </Route>
      </Route>
    </Routes>
  )
}
